import classNames from 'classnames';
import {observer} from 'mobx-react';
import React from 'react';
import {
  DragDropContext,
  Draggable,
  DraggableChildProvided,
  Droppable,
  DroppableChildProvided
} from '../../../../../../lib/components/drag-n-drop';
import {ReactNodeGenerator} from '../../../../../../lib/helpers';
import {X9ContentStateStore} from '../../../common/X9ContentStateStore';
import {MQDataPair, MQMatchingQuestion} from '../../models';

import {ignoreDroppableId, toDraggableId, toDroppableId,} from '../dnd-utils';
import {DndMatchingQuestionStore} from '../DndMatchingQuestionStore';

type DraggableRenderFn = (provided: DraggableChildProvided) => React.ReactNode;
type DroppableRenderFn = (provided: DroppableChildProvided) => React.ReactNode;

interface Props {
  id: number,
  question: MQMatchingQuestion,
  store: X9ContentStateStore,
  lessonType?: string
}

function gapFiller(item: MQDataPair): DraggableRenderFn {
  return (provided) => {
    const classname = classNames('matching-question-dnd gap-filler',
      {
        mirror: provided.isMirror,
        dragging: !provided.isMirror && provided.isDragging,
        default: !provided.isMirror && !provided.isDragging
      });
    return (
      <div className={classname}
           {...provided.draggableProps}
           {...provided.mirrorProps}>
        <i className="far fa-hand-paper"/> {item.rightContent}
      </div>
    );
  };
}


/******************************************************************************/

function renderLeftSide(store: DndMatchingQuestionStore, index: number): React.ReactNode {
  const item = store.dataPairs[index];
  const leftSideType = store.options.leftSide;

  if (leftSideType === 'text')
    return (
      <div className="left-side-text">
        {item.leftContent}
      </div>);
  if (leftSideType === 'image')
    return (
      <div className="left-side-image">
        <img className="left-side-image" src={item.leftContent} alt=""/>
      </div>);
  return null;
}

function gapTargetDroppableRenderFn(index: number,
                                    classname: string,
                                    matchedToIndex: number | null,
                                    matchedTo: MQDataPair | null): DroppableRenderFn {
  return (provided) => {
    return (
      <div className={classname} {...provided.droppableProps}>
        {
          matchedTo !== null && matchedToIndex !== null &&
          <Draggable draggableId={toDraggableId(matchedToIndex)}>
            {gapFiller(matchedTo)}
          </Draggable>
        }
        {
          matchedTo === null &&
          <div className="gap-filler-placeholder"><span>&nbsp;</span></div>
        }
      </div>);
  };
}

function renderGapTarget(store: DndMatchingQuestionStore,
                         index: number): React.ReactNode {
  const item = store.dataPairs[index];
  const state = store.dataPairStates[index];

  const filled = state.matchedTo !== null;
  const empty = state.matchedTo === null;
  const checked = filled && state.checked;
  const valid = checked && state.matchedTo === index;
  const invalid = checked && state.matchedTo !== index;

  const classname = classNames('gap-target', {
    filled: filled,
    empty: empty,
    valid: valid,
    invalid: invalid
  });

  if (valid)
    return (
      <div className={classname} key={index}>
        <div className="matching-question-dnd gap-filler">
          <i className="far fa-hand-paper"/> {item.rightContent}</div>
      </div>);

  const matchedTo = state.matchedTo === null
    ? null
    : store.dataPairs[state.matchedTo];
  const matchedToIndex = state.matchedTo === null
    ? null
    : state.matchedTo;

  return (
    <Droppable droppableId={toDroppableId(index)}>
      {gapTargetDroppableRenderFn(index, classname, matchedToIndex, matchedTo)}
    </Droppable>);
}

function renderGapFillerDroppable(index: number, item: MQDataPair): DroppableRenderFn {
  return ((provided) => {
    return (
      <div className="gap-filler-container" {...provided.droppableProps}>
        <Draggable draggableId={toDraggableId(index)}>
          {gapFiller(item)}
        </Draggable>
      </div>);
  });
}

function renderGapFiller(store: DndMatchingQuestionStore,
                         index: number): React.ReactNode {
  index = store.rightSideOrder[index];
  const item = store.dataPairs[index];
  const isUsed = store.dataPairStates.findIndex(
    (x) => x.matchedTo === index) !== -1;

  if (isUsed)
    return (
      <div className="gap-filler-container">
        <div className="gap-filling-dnd gap-filler used">
          <i className="far fa-hand-paper"/> {item.rightContent}
        </div>
      </div>
    );

  return (
    <Droppable droppableId={ignoreDroppableId(index)}>
      {renderGapFillerDroppable(index, item)}
    </Droppable>);
}

function* rendererFn(store: DndMatchingQuestionStore, lessonType?: string): ReactNodeGenerator {
  const {dataPairs} = store;

  for (let index = 0; index < dataPairs.length; index++) {
    yield (
      <div className="row align-items-center" key={index}>
        <div className="col">
          {renderLeftSide(store, index)}
        </div>
        <div className="col">
          {renderGapTarget(store, index)}
        </div>
        <div className="col">
          {renderGapFiller(store, index)}
        </div>
      </div>);
  }

  if (lessonType !== 'Examination') {
    yield (
      <div className="row justify-content-end" key="check-control">
        <div className="col-auto">
          <button type="button" className="btn btn-primary" onClick={store.check}>Check</button>
        </div>
      </div>
    );
  }
}

@observer
export class StudentDndMatchingQuestion extends React.Component<Props> {
  readonly store: DndMatchingQuestionStore;

  constructor(props: Readonly<Props>) {
    super(props);
    this.store = new DndMatchingQuestionStore(props.id, props.question, props.store);
  }

  render() {
    const store = this.store;

    if (store.options.behavior !== 'dragdrop')
      return null;

    const leftSideType = store.options.leftSide;

    const classname = classNames('matching-question-dnd', {
      text: leftSideType === 'text', image: leftSideType === 'image'
    });
    return (
      <DragDropContext canDropOn={store.canDropOn}
                       onDragUpdate={store.onDragUpdate}
                       onDragEnd={store.onDragEnd}>
        <div className="row">
          <div className="col">
            <div className={classname}>
              {Array.from(rendererFn(store, this.props.lessonType))}
            </div>
          </div>
        </div>

      </DragDropContext>);
  }
}
